import React, {useEffect, useMemo, useState} from 'react';
import {
    Box,
    Button, Checkbox,
    FormControl,
    FormControlLabel, IconButton, InputAdornment,
    InputLabel,
    List,
    ListItem,
    MenuItem,
    Select,
    Typography
} from '@mui/material';
import ApiClient from "../sdk/ApiClient";
import {useKeycloak} from "@react-keycloak/web";
import {ClearIcon} from "@mui/x-date-pickers";

const PropertySelection = ({onSelectedMembersChanged}) => {

    const {keycloak} = useKeycloak();

    // États pour gérer la sélection et l'affichage
    const [candidates, setCandidates] = useState([]);
    const [selectedCandidates, setSelectedCandidates] = useState([]);
    const [properties, setProperties] = useState([]);
    const [filters, setFilters] = useState({});

    const apiClientInstance = useMemo(() => new ApiClient(keycloak.token), [keycloak.token]);

    useEffect(() => {
        onSelectedMembersChanged(selectedCandidates);
    }, [selectedCandidates, onSelectedMembersChanged]);

    // useEffect pour exécuter fetchMissions au chargement de la page
    useEffect(() => {
        // Méthode pour récupérer les missions
        const fetchProperties = async () => {
            try {
                const properties = await apiClientInstance.getProperties()
                setProperties(properties);
                const selectProperties = properties.filter(p => p.type === "SELECT");
                const mappedValues = selectProperties.map(p => ({ id: p.id, value: null}));
                const selectMultiProperties = properties.filter(p => p.type === "SELECTMUL");
                const mappedMultiValues = selectMultiProperties.map(p => ({ id: p.id, value: []}));
                setFilters([...mappedValues, ...mappedMultiValues]);
            } catch (error) {
                console.error("Erreur lors de la récupération des propriétés :", error);
            }
        }
        fetchProperties();
    }, [apiClientInstance]);

    const resetFilter = (propertyId, newValue) => {
        setFilters(prev =>
            prev.map(p =>
                p.id === propertyId ? { ...p, value: newValue } : p
            )
        );
    };


    const handleChangeSelect = (event) => {
        const propertyId  = event.target.name ;
        const newValue  = event.target.value ;
        setFilters(prev =>
            prev.map(p =>
                p.id === propertyId ? { ...p, value: newValue } : p
            )
        );
    };

    const generateInput = (property) => {
        if(property.disabled) return ;
        if(property.type === "SELECT") {
            const labelId = `${property.apiKey}-label`;
            const selectId = `${property.apiKey}-select`;
            const propIdStr = property.id;

            return  <FormControl  key={property.id} fullWidth sx={{paddingBottom:"10px"}}>
                <InputLabel id={labelId}>{property.internal}</InputLabel>
                <Select
                    labelId={labelId}
                    id={selectId}
                    name={propIdStr}
                    value={filters?.find(p => p.id === property.id)?.value ?? ""}
                    onChange={handleChangeSelect}
                    variant={"outlined"}
                    label={property.internal}
                    endAdornment={
                        filters?.find(p => p.id === property.id)?.value ? (
                            <InputAdornment position="end">
                                <IconButton
                                    style={{marginRight:"15px"}}
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation(); // pour ne pas ouvrir le menu
                                        resetFilter(property.id, null)
                                    }}
                                >
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            </InputAdornment>
                        ) : null
                    }
                >
                    {property.choices.map(choice => (
                        <MenuItem key={choice.id} value={choice.id} >
                            {choice.labelFr}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        }
        if(property.type === "SELECTMUL") {
            const labelId = `${property.apiKey}-label`;
            const selectId = `${property.apiKey}-select`;
            const propIdStr = property.id;

            return  <FormControl  key={property.id} fullWidth sx={{paddingBottom:"10px"}}>
                <InputLabel id={labelId}>{property.internal}</InputLabel>
                <Select
                    labelId={labelId}
                    id={selectId}
                    name={propIdStr}
                    value={filters?.find(p => p.id === property.id)?.value ?? []}
                    onChange={handleChangeSelect}
                    variant={"outlined"}
                    label={property.internal}
                    multiple
                    endAdornment={
                        filters?.find(p => p.id === property.id)?.value ? (
                            <InputAdornment position="end" >
                                <IconButton
                                    style={{marginRight:"15px"}}
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation(); // pour ne pas ouvrir le menu
                                        resetFilter(property.id, [])
                                    }}
                                >
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            </InputAdornment>
                        ) : null
                    }
                >
                    {property.choices.map(choice => (
                        <MenuItem key={choice.id} value={choice.id}>
                            {choice.labelFr}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        }
    }

    const onClickRefresh = async () => {
        setSelectedCandidates([]);
        setCandidates(await apiClientInstance.getCandidatesByFilters(filters));
    }

    const onClickSelectAll = () => {
        setSelectedCandidates(candidates);
    }

    if(properties.length === 0) {
        return <div>Chargement des propriétés...</div>;
    }

    const handleMemberSelect = (candidate, checked) => {
        setSelectedCandidates(prev => {
            // suppression si décoché
            if (!checked) {
                return prev.filter(c => c.id !== candidate.id);
            }

            // ajout si coché et pas encore présent
            const exists = prev.some(c => c.id === candidate.id);
            if (exists) return prev; // déjà dedans

            return [...prev, candidate];
        });
    }

    return (
        <Box id={"properties_box"} display="flex" sx={{overflowY: "hidden"}} >
            {/* Colonne de gauche : Sélection */}
            <Box
                flex={3}
                paddingRight={2}
                borderRight="1px solid #ddd"
                sx={{overflowY: "auto"}}
                height={"75vh"}
            >
                <Typography variant="h5">Filtres</Typography>
                {/*<List>*/}
                    {properties.map((property) => {return generateInput(property); })}
                {/*</List>*/}
                <Button name={"back"} variant="outlined" onClick={onClickRefresh}>RAFRAICHIR</Button>
            </Box>
            <Box flex={9} sx={{}}>
                <div style={{display:"flex", gap:"10px", verticalAlign:"baseline", marginBottom:"10px"}}>
                    <Typography variant="h5">Sélection des candidats</Typography>
                    <Typography variant="h7">{selectedCandidates.length}/{candidates.length}</Typography>
                    <Button name={"selectAll"} variant="outlined" onClick={onClickSelectAll}>SELECTIONNER TOUS LES CANDIDATS</Button>
                </div>
                <List sx={{overflowY: "scroll", height: "75vh"}}>
                    {candidates.map((candidate) => (
                        <ListItem key={candidate.id} dense>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={
                                            selectedCandidates.find(c => c.id === candidate.id) !== undefined ||
                                            false
                                        }
                                        onChange={(e) =>
                                            handleMemberSelect(
                                                candidate,
                                                e.target.checked
                                            )
                                        }
                                    />
                                }
                                label={candidate.firstName.toUpperCase() + ' ' + candidate.lastName.toUpperCase()}
                            />
                        </ListItem>
                    ))}
                </List>
            </Box>
        </Box>
    );
};

export default PropertySelection;
